import { useState, useEffect, useRef } from "react";
import { Calendar, Tag, ArrowRight, BookOpen, Megaphone } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const categories = ["All", "Meditation", "Awareness", "Government Schemes", "Health", "Updates", "Upcoming Campaigns"];

const posts = [
  {
    title: "The Science Behind Meditation: Why It Works",
    excerpt: "Explore the neuroscience behind meditation and how regular practice rewires the brain for compassion, focus, and resilience.",
    category: "Meditation",
    date: "Jan 15, 2026",
    readTime: "5 min read",
    img: "https://images.unsplash.com/photo-1763723341417-a4ea5ba6f675?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxtZWRpdGF0aW9uJTIwc3Bpcml0dWFsJTIwd2VsbG5lc3MlMjBwZWFjZWZ1bHxlbnwwfHx8fDE3NzYwNzczMjd8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Understanding Ayushman Bharat: A Guide for Communities",
    excerpt: "A comprehensive guide to the Ayushman Bharat Yojana and how eligible families can access free healthcare services.",
    category: "Government Schemes",
    date: "Jan 10, 2026",
    readTime: "7 min read",
    img: "https://static.prod-images.emergentagent.com/jobs/928e083d-76f5-471c-8405-fe8d0803371a/images/ffab65a0e108be7611e1fac02d06405cc485f0f9bb52117ea7e290636b0e2172.png",
  },
  {
    title: "Digital Literacy: Empowering Rural Youth",
    excerpt: "How Tech Chetna is transforming lives by bringing digital skills to underserved communities and creating pathways to employment.",
    category: "Awareness",
    date: "Dec 28, 2025",
    readTime: "4 min read",
    img: "https://static.prod-images.emergentagent.com/jobs/928e083d-76f5-471c-8405-fe8d0803371a/images/31fadb22cf74a4ee8ee54b7bf56238e98a5ab6fe9fcda8b81f76add183a70b85.png",
  },
  {
    title: "Preventive Healthcare: Building Healthier Communities",
    excerpt: "Why preventive care matters more than ever, and how our Swasthya Sahayata Abhiyan is making healthcare accessible to all.",
    category: "Health",
    date: "Dec 20, 2025",
    readTime: "6 min read",
    img: "https://images.pexels.com/photos/14558557/pexels-photo-14558557.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    title: "BSM Foundation: 2025 Year in Review",
    excerpt: "A look back at the milestones achieved, communities served, and the impact created throughout the year.",
    category: "Updates",
    date: "Dec 15, 2025",
    readTime: "8 min read",
    img: "https://images.unsplash.com/photo-1524069290683-0457abfe42c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxjb21tdW5pdHklMjBzZXJ2aWNlJTIwdm9sdW50ZWVyaW5nJTIwSW5kaWF8ZW58MHx8fHwxNzc2MDc3MzI3fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Upcoming: Free Health Camp in Rural Delhi NCR",
    excerpt: "Join us for a free health check-up camp featuring general physicians, dental care, and eye check-ups for community members.",
    category: "Upcoming Campaigns",
    date: "Feb 10, 2026",
    readTime: "3 min read",
    img: "https://static.prod-images.emergentagent.com/jobs/928e083d-76f5-471c-8405-fe8d0803371a/images/02e7b62035f3925b6e35e4318c18c52079cad8f7be0eba72e55b45a1b0296bf2.png",
  },
];

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, isVisible];
}

function AnimatedSection({ children, className = "", delay = 0 }) {
  const [ref, isVisible] = useInView();
  return (
    <div ref={ref} className={`${className} transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

export default function UpdatesPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const filtered = activeCategory === "All" ? posts : posts.filter((p) => p.category === activeCategory);

  return (
    <div data-testid="updates-page" className="pt-20">
      {/* Header */}
      <section className="py-20 sm:py-28 bg-[#F3F0E6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1B201E] mb-6">Updates & Insights</h1>
            <p className="text-[#7A827E] text-base sm:text-lg max-w-3xl leading-relaxed">
              Blogs, insights, upcoming campaigns, and the latest updates from BSM Welfare Foundation.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Content */}
      <section data-testid="updates-content" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection className="mb-12">
            <div className="flex flex-wrap gap-2" data-testid="category-filters">
              {categories.map((cat) => (
                <button key={cat} onClick={() => setActiveCategory(cat)} data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, "-")}`} className={`rounded-full px-5 py-2 text-sm font-medium transition-all duration-300 ${activeCategory === cat ? "bg-[#E28743] text-white shadow-[0_4px_14px_rgba(226,135,67,0.3)]" : "bg-white text-[#7A827E] border border-[#E2E5E3] hover:border-[#E28743] hover:text-[#E28743]"}`}>
                  {cat}
                </button>
              ))}
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((post, i) => (
              <AnimatedSection key={`${activeCategory}-${i}`} delay={i * 80}>
                <Card data-testid={`blog-card-${i}`} className="bg-white border-0 rounded-3xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.04)] transition-all duration-500 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] hover:-translate-y-2 group h-full flex flex-col">
                  <div className="h-48 overflow-hidden">
                    <img src={post.img} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <CardContent className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <Badge className="bg-[#606C38]/10 text-[#606C38] border-0 rounded-full px-3 py-1 text-xs font-medium hover:bg-[#606C38]/20">{post.category}</Badge>
                      <span className="text-xs text-[#7A827E]">{post.readTime}</span>
                    </div>
                    <h3 className="font-['Outfit'] text-lg font-semibold text-[#1B201E] mb-2 line-clamp-2">{post.title}</h3>
                    <p className="text-[#7A827E] text-sm leading-relaxed mb-4 flex-1 line-clamp-3">{post.excerpt}</p>
                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-[#E2E5E3]/50">
                      <div className="flex items-center gap-1.5 text-xs text-[#7A827E]">
                        <Calendar size={12} />{post.date}
                      </div>
                      <button className="inline-flex items-center gap-1 text-sm text-[#E28743] font-medium hover:gap-2 transition-all duration-300">Read <ArrowRight size={14} /></button>
                    </div>
                  </CardContent>
                </Card>
              </AnimatedSection>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16" data-testid="no-posts">
              <BookOpen size={48} className="text-[#E2E5E3] mx-auto mb-4" />
              <p className="text-[#7A827E]">No posts found in this category.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
